import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

const AdminLogin = () => {
  const { signIn } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields')
      setLoading(false)
      return
    }

    const { success, error } = await signIn(formData)

    if (!success) {
      setError(error || 'Invalid credentials')
      setLoading(false)
    }
    // Don't navigate manually - let AuthContext and route guards handle it
    // Admin will auto-redirect to /admin-portal, regular user to /dashboard
  }

  return (
    <div className="w-full">
      {/* Admin Portal Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
          <Shield className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Admin Portal
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Sign in with your administrator credentials
        </p>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email */}
        <div className="input-group">
          <label htmlFor="email" className="input-label">
            Admin Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@universaltrustcu.com"
              className="pl-10"
              required
              autoComplete="username"
            />
          </div>
        </div>

        {/* Password */}
        <div className="input-group">
          <label htmlFor="password" className="input-label">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter admin password"
              className="pl-10 pr-10"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="btn btn-danger w-full btn-lg"
        >
          {loading ? (
            <>
              <div className="spinner w-5 h-5" />
              Authenticating...
            </>
          ) : (
            <>
              <Shield className="w-5 h-5" />
              Admin Sign In
            </>
          )}
        </button>
      </form>

      {/* Security Note */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600">
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          <strong>Security Notice:</strong> This portal is for authorized administrators only.
        </p>
      </div>

      {/* Back to Main Site */}
      <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
        Not an administrator?{' '}
        <Link
          to="/login"
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
        >
          User Login
        </Link>
        {' or '}
        <Link
          to="/"
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
        >
          Return to main site
        </Link>
      </p>
    </div>
  )
}

export default AdminLogin